// Ocean Insight Pro Mobile - AI Fishing Report Assistant
// AI-powered fishing report generation based on ocean data analysis

import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, KeyboardAvoidingView, Platform, Modal, SafeAreaView, Share, Alert } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Location from 'expo-location';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { Card, Title, Paragraph, Button, Chip, useTheme, IconButton, Surface, Divider, ProgressBar, Menu, Switch, RadioButton } from 'react-native-paper';
import { MaterialIcons } from '@expo/vector-icons';
import { PlatformMap } from '../components/PlatformMap';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { setMapLocation } from '../store/mapSlice';
import { reportGeneratorService } from '../services/reportGenerator';
import { ReportRequest as ServiceReportRequest, ProcessingStatus, FishingReport } from '../services/reportGenerator/types';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '../types';
import { DatasetSelector } from '../components/DatasetSelector';
import { Portal } from 'react-native-paper';
import { useAuth } from '../contexts/AuthContext';

interface ReportRequest {
  area: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  date: Date;
  targetSpecies?: string[];
  includeWeatherForecast: boolean;
  includeHistoricalPatterns: boolean;
}

interface SavedReport {
  reportId: string;
  generatedAt: string;
  summary: string;
  location: {
    latitude: number;
    longitude: number;
  };
  hotspotCount: number;
  confidence: number;
}

export default function TripsScreen() {
  const theme = useTheme();
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<'assistant' | 'reports'>('assistant');
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportId, setReportId] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
  const [generatedReport, setGeneratedReport] = useState<FishingReport | null>(null);
  
  // Map selection state
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [mapCenter, setMapCenter] = useState({ lat: 27.8, lng: -97.0 });
  const [zoom, setZoom] = useState(9);
  const [mapRegion, setMapRegion] = useState({
    latitude: 27.6169,
    longitude: -97.2062,
    latitudeDelta: 2,
    longitudeDelta: 2,
  });
  
  // Store the analysis area separately so it persists
  const [analysisArea, setAnalysisArea] = useState<{ latitude: number; longitude: number } | null>(null);
  
  // Get location preferences from Redux
  const locationPreferences = useSelector((state: RootState) => state.user.locationPreferences);
  const hasLocation = locationPreferences.useCurrentLocation || locationPreferences.savedLocation;
  
  // Initialize selected location and map region from user preferences
  useEffect(() => {
    if (locationPreferences?.savedLocation?.coordinates?.latitude && locationPreferences?.savedLocation?.coordinates?.longitude) {
      const location = {
        latitude: locationPreferences.savedLocation.coordinates.latitude,
        longitude: locationPreferences.savedLocation.coordinates.longitude
      };
      setSelectedLocation(location);
      setMapRegion({
        latitude: location.latitude,
        longitude: location.longitude,
        latitudeDelta: 2,
        longitudeDelta: 2,
      });
    }
  }, [locationPreferences]);
  
  // Saved reports state
  const [savedReports, setSavedReports] = useState<SavedReport[]>([]);
  const [menuVisible, setMenuVisible] = useState<{ [key: string]: boolean }>({});
  
  // Dataset selection state
  const [showDatasetSelector, setShowDatasetSelector] = useState(false);
  const [selectedDatasets, setSelectedDatasets] = useState<{
    chlorophyll: { dataset: string; date: Date };
    sst: { dataset: string; date: Date };
  } | null>(null);

  // Waypoint options state
  const [useWaypoints, setUseWaypoints] = useState(true);
  const [waypointType, setWaypointType] = useState<'public' | 'personal' | 'both'>('public');
  const { user: currentUser } = useAuth();
  
  // Load saved reports on component mount
  useEffect(() => {
    loadSavedReports();
  }, []);
  
  const loadSavedReports = async () => {
    try {
      const reportsJson = await AsyncStorage.getItem('savedReports');
      if (reportsJson) {
        const reports = JSON.parse(reportsJson);
        setSavedReports(reports);
      }
    } catch (error) {
      console.error('Failed to load saved reports:', error);
    }
  };
  
  const saveReport = async (report: FishingReport) => {
    try {
      const newReport: SavedReport = {
        reportId: report.reportId,
        generatedAt: new Date(report.generatedAt).toISOString(),
        summary: report.executiveSummary.overall,
        location: {
          latitude: analysisArea?.latitude || selectedLocation?.latitude || 0,
          longitude: analysisArea?.longitude || selectedLocation?.longitude || 0
        },
        hotspotCount: report.fishingPredictions.hotspots.length,
        confidence: report.executiveSummary.confidence
      };
      
      // Get existing reports
      let reports = [...savedReports];
      
      // Add new report at the beginning
      reports.unshift(newReport);
      
      // Keep only the last 5 reports
      if (reports.length > 5) {
        reports = reports.slice(0, 5);
      }
      
      // Save to AsyncStorage
      await AsyncStorage.setItem('savedReports', JSON.stringify(reports));
      setSavedReports(reports);
    } catch (error) {
      console.error('Failed to save report:', error);
    }
  };
  
  const shareReport = async (report: SavedReport) => {
    try {
      const reportDate = new Date(report.generatedAt);
      const dateStr = reportDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
      
      let hotspotsText = '';
      
      // Try to fetch full report data to get all hotspot coordinates
      try {
        const fullReport = await reportGeneratorService.getReport(report.reportId);
        if (fullReport && fullReport.fishingPredictions?.hotspots?.length > 0) {
          // Build detailed hotspots list with coordinates
          hotspotsText = '\n📍 Fishing Hotspots:\n';
          fullReport.fishingPredictions.hotspots.forEach((hotspot, index) => {
            hotspotsText += `${index + 1}. ${hotspot.location.lat.toFixed(4)}°, ${hotspot.location.lon.toFixed(4)}° (${Math.round(hotspot.confidence * 100)}% confidence)\n`;
            if (hotspot.waterConditions.temperature) {
              hotspotsText += `   Water Temp: ${hotspot.waterConditions.temperature.toFixed(1)}°C\n`;
            }
          });
        }
      } catch (fetchError) {
        console.log('Could not fetch full report, using saved data');
      }
      
      const message = `🎣 Ocean Insight Pro - Fishing Report\n\n` +
        `📅 ${dateStr}\n` +
        `📍 Analysis Center: ${report.location.latitude.toFixed(4)}°, ${report.location.longitude.toFixed(4)}°\n` +
        `🎯 ${report.hotspotCount} Hotspots Found\n${ 
        hotspotsText 
        }\n📊 Summary:\n${report.summary}\n\n` +
        `Generated by Ocean Insight Pro`;
      
      const result = await Share.share({
        message,
        title: 'Fishing Report',
      }, {
        dialogTitle: 'Share Fishing Report',
        subject: `Fishing Report - ${dateStr}`, // For email
      });
      
      if (result.action === Share.sharedAction) {
        console.log('Shared successfully');
      } else if (result.action === Share.dismissedAction) {
        console.log('Share dismissed');
      }
    } catch (error) {
      console.error('Error sharing report:', error);
      Alert.alert('Error', 'Unable to share report');
    }
  };
  
  const exportToGPX = async (report: SavedReport) => {
    try {
      const reportDate = new Date(report.generatedAt);
      const dateStr = reportDate.toISOString();
      
      // Build waypoints
      let waypoints = '';
      
      // Always add analysis center waypoint
      waypoints += `  <wpt lat="${report.location.latitude}" lon="${report.location.longitude}">
    <name>Analysis Center</name>
    <desc>Center of analysis area - ${report.hotspotCount} hotspots found</desc>
    <time>${dateStr}</time>
    <sym>Flag</sym>
  </wpt>\n`;
      
      // Try to fetch full report data to get all hotspot coordinates
      try {
        const fullReport = await reportGeneratorService.getReport(report.reportId);
        if (fullReport && fullReport.fishingPredictions?.hotspots?.length > 0) {
          // Add detailed hotspot waypoints
          fullReport.fishingPredictions.hotspots.forEach((hotspot, index) => {
            const confidence = Math.round(hotspot.confidence * 100);
            let desc = `Confidence: ${confidence}%`;
            if (hotspot.waterConditions.temperature) {
              desc += `, Water Temp: ${hotspot.waterConditions.temperature.toFixed(1)}°C`;
            }
            if (hotspot.reasons && hotspot.reasons.length > 0) {
              desc += `, ${hotspot.reasons[0]}`;
            }
            
            waypoints += `  <wpt lat="${hotspot.location.lat}" lon="${hotspot.location.lon}">
    <name>Hotspot ${index + 1}</name>
    <desc>${desc}</desc>
    <time>${dateStr}</time>
    <sym>Fish</sym>
    <extensions>
      <confidence>${confidence}</confidence>
      <score>${hotspot.score}</score>
    </extensions>
  </wpt>\n`;
          });
        } else {
          // Fallback: Add a note that detailed hotspots are not available
          waypoints += `  <!-- Note: Detailed hotspot coordinates not available. Visit the app for full report. -->\n`;
        }
      } catch (fetchError) {
        console.log('Could not fetch full report for GPX export');
        // Add a comment in the GPX file
        waypoints += `  <!-- Note: Full report data unavailable. Showing analysis center only. -->\n`;
      }
      
      // Create GPX content
      const gpxContent = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Ocean Insight Pro"
  xmlns="http://www.topografix.com/GPX/1/1"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
    <name>Fishing Report - ${dateStr}</name>
    <desc>${report.summary}</desc>
    <time>${dateStr}</time>
    <author>
      <name>Ocean Insight Pro</name>
    </author>
  </metadata>
${waypoints}</gpx>`;
      
      // Save to file
      const fileName = `fishing-report-${reportDate.getTime()}.gpx`;
      const fileUri = FileSystem.documentDirectory + fileName;
      
      await FileSystem.writeAsStringAsync(fileUri, gpxContent, {
        encoding: FileSystem.EncodingType.UTF8,
      });
      
      // Share the file
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(fileUri, {
          mimeType: 'application/gpx+xml',
          dialogTitle: 'Export Fishing Report GPX',
        });
      } else {
        Alert.alert('Error', 'Sharing is not available on this device');
      }
    } catch (error) {
      console.error('Error exporting GPX:', error);
      Alert.alert('Error', 'Failed to export GPX file');
    }
  };


  const generateReport = async () => {
    // Show dataset selector first
    setShowDatasetSelector(true);
  };

  const generateReportWithDatasets = async (datasets?: {
    chlorophyll: { dataset: string; date: Date };
    sst: { dataset: string; date: Date };
  }) => {
    // Use passed datasets or fall back to state
    const datasetsToUse = datasets || selectedDatasets;
    try {
      setIsGenerating(true);
      setProcessingStatus(null);
      setGeneratedReport(null);
      
      // Build report request
      const location = analysisArea || selectedLocation;
      console.log('Analysis location:', location);
      
      let bounds;
      if (location) {
        // Use the user-selected location from map
        // For Texas Gulf Coast, water is to the EAST (less negative longitude)
        const isTexasCoast = location.longitude > -98 && location.longitude < -96 && 
                            location.latitude > 26 && location.latitude < 30;
        
        if (isTexasCoast) {
          // Texas Gulf Coast - extend eastward into the Gulf
          bounds = {
            north: location.latitude + 1.25,
            south: location.latitude - 1.25,
            east: location.longitude + 2.5,  // Go 2.5 degrees east into Gulf for Texas coast
            west: location.longitude         // Start at coast
          };
        } else {
          // Default bounds for other areas - 2.5 degrees x 2.5 degrees
          bounds = {
            north: location.latitude + 1.25,
            south: location.latitude - 1.25,
            east: location.longitude + 1.25,
            west: location.longitude - 1.25
          };
        }
      } else if (locationPreferences?.savedLocation?.coordinates?.latitude && locationPreferences?.savedLocation?.coordinates?.longitude) {
        const savedLoc = locationPreferences.savedLocation.coordinates;
        const isTexasCoast = savedLoc.longitude > -98 && savedLoc.longitude < -96 && 
                            savedLoc.latitude > 26 && savedLoc.latitude < 30;
        
        if (isTexasCoast) {
          // Texas Gulf Coast - extend eastward into the Gulf
          bounds = {
            north: savedLoc.latitude + 1.25,
            south: savedLoc.latitude - 1.25,
            east: savedLoc.longitude + 2.5,  // Go 2.5 degrees east into Gulf for Texas coast
            west: savedLoc.longitude         // Start at coast
          };
        } else {
          bounds = {
            north: savedLoc.latitude + 1.25,
            south: savedLoc.latitude - 1.25,
            east: savedLoc.longitude + 1.25,
            west: savedLoc.longitude - 1.25
          };
        }
      } else {
        // Default to Florida Keys area - 2.5 x 2.5 degrees
        bounds = {
          north: 28.25,
          south: 25.75,
          east: -78.75,
          west: -81.25
        };
      }
      
      console.log('Using bounds:', bounds);
      
      const request: ServiceReportRequest = {
        bounds,
        dateRange: {
          start: datasetsToUse?.chlorophyll.date || new Date(),
          end: new Date((datasetsToUse?.chlorophyll.date || new Date()).getTime() + 3 * 24 * 60 * 60 * 1000)
        },
        includeHistorical: false,
        reportType: 'advanced',
        datasetPreferences: datasetsToUse ? {
          chlorophyll: datasetsToUse.chlorophyll.dataset,
          sst: datasetsToUse.sst.dataset
        } : undefined,
        datasetDates: datasetsToUse ? {
          chlorophyll: datasetsToUse.chlorophyll.date,
          sst: datasetsToUse.sst.date
        } : undefined,
        waypointOptions: useWaypoints ? {
          useWaypoints: true,
          waypointType,
          userId: currentUser?.uid
        } : undefined
      };
      
      // Start report generation
      const newReportId = await reportGeneratorService.generateReport(request);
      setReportId(newReportId);
      
      // Poll for status updates
      const pollInterval = setInterval(async () => {
        const status = await reportGeneratorService.getReportStatus(newReportId);
        if (status) {
          setProcessingStatus(status);
          
          if (status.status === 'complete') {
            clearInterval(pollInterval);
            console.log('Report status is complete, fetching report...');
            const report = await reportGeneratorService.getReport(newReportId);
            console.log('Report retrieved:', report ? 'Success' : 'Failed');
            if (report) {
              setGeneratedReport(report);
              setIsGenerating(false);
              console.log('Report set in state, UI should update');
              // Save the report automatically
              await saveReport(report);
            } else {
              console.error('Report was null despite complete status');
              setIsGenerating(false);
            }
          } else if (status.status === 'error') {
            clearInterval(pollInterval);
            setIsGenerating(false);
            console.error('Report generation failed:', status.error);
          }
        }
      }, 2000);
      
      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setIsGenerating(false);
      }, 300000);
      
    } catch (error) {
      console.error('Failed to generate report:', error);
      setIsGenerating(false);
    }
  };

  const renderAssistant = () => (
    <KeyboardAvoidingView 
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.assistantContainer}
    >
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* AI Assistant Header */}
        <Card style={styles.headerCard}>
          <Card.Content>
            <View style={styles.assistantHeader}>
              <MaterialIcons name="auto-awesome" size={32} color={theme.colors.primary} />
              <View style={styles.headerText}>
                <Title style={styles.assistantTitle}>AI Fishing Report</Title>
                <Paragraph style={styles.assistantSubtitle}>
                  Get AI-powered fishing predictions based on real-time ocean data
                </Paragraph>
              </View>
            </View>
          </Card.Content>
        </Card>


        {/* Quick Options */}
        <Card style={styles.card}>
          <Card.Content>
            <Title style={styles.sectionTitle}>Select Fishing Location</Title>
            
            {/* Area Selection */}
            <View style={styles.paramRow}>
              <MaterialIcons name="map" size={24} color={theme.colors.onSurfaceVariant} />
              <View style={styles.paramContent}>
                <Paragraph style={styles.paramLabel}>Analysis Area</Paragraph>
                <Paragraph style={styles.helpText}>
                  💡 Tip: Select locations further offshore for better ocean data and more accurate fishing predictions
                </Paragraph>
                <Button 
                  mode="contained" 
                  onPress={async () => {
                    // Try to get user's current location when opening map
                    try {
                      const { status } = await Location.requestForegroundPermissionsAsync();
                      if (status === 'granted') {
                        const location = await Location.getCurrentPositionAsync({});
                        // Set both mapCenter and mapRegion to user's location
                        setMapCenter({
                          lat: location.coords.latitude,
                          lng: location.coords.longitude
                        });
                        setMapRegion({
                          latitude: location.coords.latitude,
                          longitude: location.coords.longitude,
                          latitudeDelta: 2,
                          longitudeDelta: 2,
                        });
                        // Don't set selectedLocation yet - let user choose
                      }
                    } catch (error) {
                      console.log('Could not get current location:', error);
                      // Use existing region or default
                    }
                    setShowMapModal(true);
                  }}
                  style={styles.areaButton}
                  icon="map-marker"
                >
                  {analysisArea || selectedLocation 
                    ? `${(analysisArea || selectedLocation)!.latitude.toFixed(4)}°, ${(analysisArea || selectedLocation)!.longitude.toFixed(4)}°`
                    : hasLocation 
                      ? locationPreferences.useCurrentLocation ? 'Select Report Location' : locationPreferences.savedLocation?.name || 'Saved location'
                      : 'Select on map'
                  }
                </Button>
              </View>
            </View>

          </Card.Content>
        </Card>

        {/* Waypoint Options Card */}
        <Card style={styles.card}>
          <Card.Content>
            <View style={styles.sectionHeader}>
              <MaterialIcons name="place" size={24} color={theme.colors.primary} />
              <Title style={styles.sectionTitle}>Structure Analysis</Title>
            </View>
            <View style={styles.waypointOptions}>
              <View style={styles.switchRow}>
                <Paragraph>Include nearby structures in ranking</Paragraph>
                <Switch
                  value={useWaypoints}
                  onValueChange={setUseWaypoints}
                  color={theme.colors.primary}
                />
              </View>
              {useWaypoints && (
                <View style={styles.radioGroup}>
                  <RadioButton.Group
                    onValueChange={value => setWaypointType(value as 'public' | 'personal' | 'both')}
                    value={waypointType}
                  >
                    <View style={styles.radioRow}>
                      <RadioButton.Android value="public" color={theme.colors.primary} />
                      <Paragraph>Public waypoints (reefs, wrecks, rigs)</Paragraph>
                    </View>
                    <View style={styles.radioRow}>
                      <RadioButton.Android value="personal" color={theme.colors.primary} />
                      <Paragraph>My personal waypoints</Paragraph>
                    </View>
                    <View style={styles.radioRow}>
                      <RadioButton.Android value="both" color={theme.colors.primary} />
                      <Paragraph>Both public and personal</Paragraph>
                    </View>
                  </RadioButton.Group>
                </View>
              )}
              <Paragraph style={styles.helperText}>
                Hotspots near structures are given higher priority as fish tend to congregate around reefs, wrecks, and platforms.
              </Paragraph>
            </View>
          </Card.Content>
        </Card>

        {/* Generate Button */}
        <Button
          mode="contained"
          onPress={generateReport}
          loading={isGenerating}
          disabled={isGenerating || (!analysisArea && !selectedLocation)}
          style={styles.generateButton}
          contentStyle={styles.generateButtonContent}
        >
          {isGenerating ? 'Analyzing Ocean Data...' : 'Generate Fishing Report'}
        </Button>

        {isGenerating && processingStatus && (
          <Card style={styles.progressCard}>
            <Card.Content>
              <Paragraph style={styles.progressText}>{processingStatus.currentStep}</Paragraph>
              <ProgressBar progress={processingStatus.progress / 100} style={styles.progressBar} />
            </Card.Content>
          </Card>
        )}
        
        {generatedReport && (
          <Card style={styles.reportCard}>
            <Card.Content>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
                <MaterialIcons name="check-circle" size={24} color={theme.colors.primary} />
                <Title style={{ marginLeft: 8 }}>Report Ready!</Title>
              </View>
              
              <Paragraph style={styles.reportSummary}>
                {generatedReport.executiveSummary.overall}
              </Paragraph>
              
              <View style={{ flexDirection: 'row', gap: 8, marginTop: 16 }}>
                <Button 
                  mode="contained" 
                  style={{ flex: 1 }}
                  onPress={() => {
                    if (reportId) {
                      navigation.navigate('ReportDetail', { reportId });
                      // Reset the state for next report
                      setGeneratedReport(null);
                      setReportId(null);
                      setAnalysisArea(null);
                      setSelectedLocation(null);
                    }
                  }}
                >
                  View Full Report
                </Button>
                <Button 
                  mode="contained" 
                  style={{ flex: 1 }}
                  onPress={() => {
                    if (generatedReport && generatedReport.fishingPredictions.hotspots.length > 0) {
                      // Convert hotspots to the format expected by MapScreen
                      const hotspotLocations = generatedReport.fishingPredictions.hotspots.map((h, index) => ({
                        latitude: h.location.lat,
                        longitude: h.location.lon,
                        title: `Hotspot #${index + 1}`,
                        temperature: h.waterConditions.temperature,
                        chlorophyll: h.waterConditions.chlorophyll,
                        currentSpeed: h.waterConditions.currentSpeed,
                        currentDirection: h.waterConditions.currentDirection,
                        confidence: h.confidence,
                        score: h.score,
                        reasons: h.reasons,
                        gradients: h.waterConditions.gradients,
                        nearbyStructures: h.nearbyStructures
                      }));
                      
                      // Calculate center point for all hotspots
                      const lats = hotspotLocations.map(h => h.latitude);
                      const lngs = hotspotLocations.map(h => h.longitude);
                      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
                      const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
                      
                      // Set the center location in Redux
                      dispatch(setMapLocation({
                        latitude: centerLat,
                        longitude: centerLng
                      }));
                      
                      // Navigate to the Map tab with hotspots
                      navigation.navigate('Home', { 
                        screen: 'Map',
                        params: {
                          hotspots: hotspotLocations,
                          enableClustering: true
                        }
                      } as any);
                      
                      // Reset the state for next report
                      setGeneratedReport(null);
                      setReportId(null);
                      setAnalysisArea(null);
                      setSelectedLocation(null);
                    }
                  }}
                  icon="map"
                >
                  View on Map
                </Button>
              </View>
              
            </Card.Content>
          </Card>
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  );

  const renderReports = () => (
    <ScrollView style={styles.scrollView}>
      {savedReports.length > 0 && (
        <View style={styles.reportCountContainer}>
          <Paragraph style={styles.reportCountText}>
            {savedReports.length} of 5 reports saved
          </Paragraph>
        </View>
      )}
      {savedReports.map((report, index) => {
        const reportDate = new Date(report.generatedAt);
        return (
          <Card key={report.reportId} style={styles.reportCard}>
            <Card.Content>
              <View style={styles.reportHeader}>
                <View style={styles.reportInfo}>
                  <Title style={styles.reportDate}>
                    {reportDate.toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </Title>
                  <Paragraph style={styles.reportTime}>
                    Generated at {reportDate.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </Paragraph>
                </View>
              </View>

              <Paragraph style={styles.reportSummary} numberOfLines={2}>
                {report.summary}
              </Paragraph>

              <View style={styles.reportHighlights}>
                <Surface style={styles.highlight}>
                  <MaterialIcons name="place" size={20} color={theme.colors.primary} />
                  <Paragraph style={styles.highlightText}>
                    {report.hotspotCount} Hotspots
                  </Paragraph>
                </Surface>
                <Surface style={styles.highlight}>
                  <MaterialIcons name="location-on" size={20} color={theme.colors.primary} />
                  <Paragraph style={styles.highlightText}>
                    {report.location.latitude.toFixed(2)}°, {report.location.longitude.toFixed(2)}°
                  </Paragraph>
                </Surface>
              </View>

              <View style={styles.reportActions}>
                <View style={{ flexDirection: 'row', gap: 8, flex: 1 }}>
                  <Button 
                    mode="outlined" 
                    onPress={() => {
                      navigation.navigate('ReportDetail', { reportId: report.reportId });
                    }}
                    compact
                    style={{ flex: 1 }}
                  >
                    View Full Report
                  </Button>
                  <Button 
                    mode="outlined" 
                    onPress={async () => {
                      // Fetch the full report to get hotspots
                      try {
                        const fullReport = await reportGeneratorService.getReport(report.reportId);
                        if (fullReport && fullReport.fishingPredictions.hotspots.length > 0) {
                          // Convert hotspots to the format expected by MapScreen
                          const hotspotLocations = fullReport.fishingPredictions.hotspots.map((h, index) => ({
                            latitude: h.location.lat,
                            longitude: h.location.lon,
                            title: `Hotspot #${index + 1}`,
                            temperature: h.waterConditions.temperature,
                            chlorophyll: h.waterConditions.chlorophyll,
                            currentSpeed: h.waterConditions.currentSpeed,
                            currentDirection: h.waterConditions.currentDirection,
                            confidence: h.confidence,
                            score: h.score,
                            reasons: h.reasons,
                            gradients: h.waterConditions.gradients,
                            nearbyStructures: h.nearbyStructures
                          }));
                          
                          // Calculate center point for all hotspots
                          const lats = hotspotLocations.map(h => h.latitude);
                          const lngs = hotspotLocations.map(h => h.longitude);
                          const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
                          const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
                          
                          // Set the center location in Redux
                          dispatch(setMapLocation({
                            latitude: centerLat,
                            longitude: centerLng
                          }));
                          
                          // Navigate to the Map tab with hotspots
                          navigation.navigate('Home', { 
                            screen: 'Map',
                            params: {
                              hotspots: hotspotLocations,
                              enableClustering: true
                            }
                          } as any);
                        }
                      } catch (error) {
                        console.error('Error loading report for map view:', error);
                      }
                    }}
                    compact
                    style={{ flex: 1 }}
                    icon="map"
                  >
                    View on Map
                  </Button>
                </View>
                <View style={styles.menuContainer}>
                  <Menu
                    visible={menuVisible[report.reportId] || false}
                    onDismiss={() => setMenuVisible({ ...menuVisible, [report.reportId]: false })}
                    anchor={
                      <IconButton
                        icon="dots-vertical"
                        size={24}
                        onPress={() => setMenuVisible({ ...menuVisible, [report.reportId]: true })}
                      />
                    }
                  >
                    <Menu.Item 
                      onPress={() => {
                        setMenuVisible({ ...menuVisible, [report.reportId]: false });
                        shareReport(report);
                      }} 
                      title="Share Report"
                      leadingIcon="share"
                    />
                    <Menu.Item 
                      onPress={async () => {
                        setMenuVisible({ ...menuVisible, [report.reportId]: false });
                        // Fetch the full report to get hotspots
                        try {
                          const fullReport = await reportGeneratorService.getReport(report.reportId);
                          if (fullReport && fullReport.fishingPredictions.hotspots.length > 0) {
                            // Convert hotspots to the format expected by MapScreen
                            const hotspotLocations = fullReport.fishingPredictions.hotspots.map((h, index) => ({
                              latitude: h.location.lat,
                              longitude: h.location.lon,
                              title: `Hotspot #${index + 1}`,
                              temperature: h.waterConditions.temperature,
                              chlorophyll: h.waterConditions.chlorophyll,
                              currentSpeed: h.waterConditions.currentSpeed,
                              currentDirection: h.waterConditions.currentDirection,
                              confidence: h.confidence,
                              score: h.score,
                              reasons: h.reasons,
                              gradients: h.waterConditions.gradients,
                              nearbyStructures: h.nearbyStructures
                            }));
                            
                            // Calculate center point for all hotspots
                            const lats = hotspotLocations.map(h => h.latitude);
                            const lngs = hotspotLocations.map(h => h.longitude);
                            const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
                            const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
                            
                            // Set the center location in Redux
                            dispatch(setMapLocation({
                              latitude: centerLat,
                              longitude: centerLng
                            }));
                            
                            // Navigate to the Map tab with hotspots
                            navigation.navigate('Home', { 
                              screen: 'Map',
                              params: {
                                hotspots: hotspotLocations,
                                enableClustering: true
                              }
                            } as any);
                          }
                        } catch (error) {
                          console.error('Error loading report for map view:', error);
                        }
                      }} 
                      title="View on Map"
                      leadingIcon="map"
                    />
                    <Menu.Item 
                      onPress={() => {
                        setMenuVisible({ ...menuVisible, [report.reportId]: false });
                        exportToGPX(report);
                      }} 
                      title="Export GPX"
                      leadingIcon="map-marker-path"
                    />
                    <Divider />
                    <Menu.Item 
                      onPress={async () => {
                        setMenuVisible({ ...menuVisible, [report.reportId]: false });
                        // Remove this report
                        const updatedReports = savedReports.filter(r => r.reportId !== report.reportId);
                        setSavedReports(updatedReports);
                        await AsyncStorage.setItem('savedReports', JSON.stringify(updatedReports));
                      }} 
                      title="Delete"
                      leadingIcon="delete"
                    />
                  </Menu>
                </View>
              </View>
            </Card.Content>
          </Card>
        );
      })}

      {savedReports.length === 0 && (
        <Card style={styles.emptyCard}>
          <Card.Content style={styles.emptyState}>
            <MaterialIcons name="article" size={64} color={theme.colors.onSurfaceVariant} />
            <Title>No reports yet</Title>
            <Paragraph>Generate your first AI fishing report to get started</Paragraph>
          </Card.Content>
        </Card>
      )}
    </ScrollView>
  );

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Tab Navigation */}
      <View style={styles.tabContainer}>
        <Chip
          selected={activeTab === 'assistant'}
          onPress={() => setActiveTab('assistant')}
          style={styles.tabChip}
        >
          AI Assistant
        </Chip>
        <Chip
          selected={activeTab === 'reports'}
          onPress={() => {
            setActiveTab('reports');
            loadSavedReports(); // Refresh reports when switching tabs
          }}
          style={styles.tabChip}
        >
          Saved Reports
        </Chip>
      </View>

      {activeTab === 'assistant' ? renderAssistant() : renderReports()}
      
      {/* Map Selection Modal */}
      <Modal
        visible={showMapModal}
        animationType="slide"
        transparent={false}
        statusBarTranslucent={true}
        onRequestClose={() => setShowMapModal(false)}
      >
        <View style={styles.mapModalContainer}>
          <View style={styles.mapFullScreen}>
            <PlatformMap
              center={mapCenter}
              zoom={zoom}
              markers={[
                // Show selected location or analysis area
                ...(selectedLocation ? [{
                  position: { lat: selectedLocation.latitude, lng: selectedLocation.longitude },
                  icon: '🎣',
                  size: [32, 32] as [number, number],
                  title: 'Fishing Location'
                }] : []),
                // Show hotspots if viewing generated report
                ...(generatedReport && generatedReport.fishingPredictions.hotspots ? 
                  generatedReport.fishingPredictions.hotspots.map((hotspot, index) => ({
                    position: { lat: hotspot.location.lat, lng: hotspot.location.lon },
                    icon: '🔥',
                    size: [30, 30] as [number, number],
                    title: `Hotspot ${index + 1} (${Math.round(hotspot.confidence * 100)}% confidence)`
                  })) : [])
              ]}
              overlays={[]}
              shapes={[]}
              baseMapType="ocean"
              onMapClick={(lat, lng) => {
                // Only allow clicking to select location if not viewing report hotspots
                if (!generatedReport) {
                  console.log('Map clicked:', lat, lng);
                  setSelectedLocation({ latitude: lat, longitude: lng });
                }
              }}
            />
          </View>
          
          <SafeAreaView style={styles.mapControlsContainer}>
            <IconButton
              icon="close"
              size={28}
              style={styles.mapCloseButton}
              onPress={() => setShowMapModal(false)}
            />
            <View style={styles.mapFooter}>
              {generatedReport ? (
                <>
                  <Title style={styles.mapHotspotsTitle}>
                    Fishing Hotspots ({generatedReport.fishingPredictions.hotspots.length})
                  </Title>
                  <Paragraph style={styles.mapInstruction}>
                    🔥 = High confidence fishing location based on ocean conditions
                  </Paragraph>
                  <View style={styles.mapButtonContainer}>
                    <Button
                      mode="contained"
                      onPress={() => setShowMapModal(false)}
                      style={styles.mapConfirmButton}
                    >
                      Close Map
                    </Button>
                  </View>
                </>
              ) : (
                <>
                  <Paragraph style={styles.mapInstruction}>
                    Tap on the map to select your fishing location
                  </Paragraph>
                  {selectedLocation && (
                    <Paragraph style={styles.mapCoordinates}>
                      {selectedLocation.latitude.toFixed(4)}°N, {selectedLocation.longitude.toFixed(4)}°W
                    </Paragraph>
                  )}
                  <View style={styles.mapButtonContainer}>
                    <Button
                      mode="outlined"
                      onPress={async () => {
                        try {
                          const { status } = await Location.requestForegroundPermissionsAsync();
                          if (status === 'granted') {
                            const location = await Location.getCurrentPositionAsync({});
                            const currentLoc = {
                              latitude: location.coords.latitude,
                              longitude: location.coords.longitude
                            };
                            setSelectedLocation(currentLoc);
                            // Update both mapCenter and mapRegion
                            setMapCenter({
                              lat: currentLoc.latitude,
                              lng: currentLoc.longitude
                            });
                            setMapRegion({
                              ...currentLoc,
                              latitudeDelta: 0.5,
                              longitudeDelta: 0.5,
                            });
                          }
                        } catch (error) {
                          console.log('Could not get current location:', error);
                        }
                      }}
                      icon="crosshairs-gps"
                      style={styles.currentLocationButton}
                    >
                      Use Current Location
                    </Button>
                    <Button
                      mode="contained"
                      onPress={() => {
                        if (selectedLocation) {
                          setAnalysisArea(selectedLocation);
                        }
                        setShowMapModal(false);
                      }}
                      disabled={!selectedLocation}
                      style={styles.mapConfirmButton}
                    >
                      Confirm Location
                    </Button>
                  </View>
                </>
              )}
            </View>
          </SafeAreaView>
        </View>
      </Modal>
      
      {/* Dataset Selector Modal */}
      <Portal>
        <Modal
          visible={showDatasetSelector}
          onDismiss={() => setShowDatasetSelector(false)}
          style={styles.datasetSelectorModal}
        >
          <DatasetSelector
            bounds={{
              minLat: (analysisArea?.latitude || selectedLocation?.latitude || 21) - 1.0,
              maxLat: (analysisArea?.latitude || selectedLocation?.latitude || 21) + 1.0,
              minLon: (analysisArea?.longitude || selectedLocation?.longitude || -157) - 1.0,
              maxLon: (analysisArea?.longitude || selectedLocation?.longitude || -157) + 1.0
            }}
            onSelectDatasets={(datasets) => {
              setSelectedDatasets(datasets);
              setShowDatasetSelector(false);
              // Proceed with report generation, passing datasets directly
              generateReportWithDatasets(datasets);
            }}
            onCancel={() => setShowDatasetSelector(false)}
          />
        </Modal>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  tabContainer: {
    flexDirection: 'row',
    padding: 16,
    gap: 8,
  },
  tabChip: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  assistantContainer: {
    flex: 1,
  },
  headerCard: {
    margin: 16,
    marginBottom: 8,
  },
  assistantHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  headerText: {
    flex: 1,
  },
  assistantTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  assistantSubtitle: {
    fontSize: 14,
    opacity: 0.7,
  },
  card: {
    margin: 16,
    marginTop: 8,
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  waypointOptions: {
    gap: 12,
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  radioGroup: {
    marginLeft: 16,
    gap: 8,
  },
  radioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  helperText: {
    fontSize: 13,
    opacity: 0.7,
    marginTop: 8,
    lineHeight: 18,
  },
  queryInput: {
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    minHeight: 80,
    textAlignVertical: 'top',
  },
  paramRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
    paddingVertical: 8,
  },
  paramContent: {
    flex: 1,
  },
  paramLabel: {
    fontSize: 14,
    marginBottom: 8,
    opacity: 0.7,
  },
  dateButton: {
    alignSelf: 'flex-start',
  },
  areaButton: {
    alignSelf: 'stretch',
    marginTop: 8,
  },
  speciesChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  speciesChip: {
    marginBottom: 4,
  },
  divider: {
    marginVertical: 8,
  },
  generateButton: {
    margin: 16,
    marginTop: 8,
  },
  generateButtonContent: {
    paddingVertical: 8,
  },
  progressCard: {
    margin: 16,
    marginTop: 0,
  },
  progressText: {
    fontSize: 14,
    marginBottom: 8,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
  },
  reportCard: {
    margin: 16,
    marginTop: 8,
    marginBottom: 8,
  },
  reportHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  reportInfo: {
    flex: 1,
  },
  reportDate: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  reportTime: {
    fontSize: 14,
    opacity: 0.7,
  },
  reportSummary: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  reportHighlights: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  highlight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    elevation: 1,
  },
  highlightText: {
    fontSize: 12,
    fontWeight: '500',
  },
  reportActions: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  emptyCard: {
    margin: 16,
  },
  emptyState: {
    alignItems: 'center',
    padding: 32,
  },
  mapModalContainer: {
    flex: 1,
    backgroundColor: '#fff',
  },
  datasetSelectorModal: {
    margin: 0,
  },
  mapFullScreen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  mapControlsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'transparent',
  },
  mapHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingBottom: 8,
    paddingTop: 8,
  },
  map: {
    flex: 1,
  },
  mapFooter: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: -2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 5,
  },
  mapInstruction: {
    textAlign: 'center',
    marginBottom: 8,
    fontSize: 14,
    color: '#666',
  },
  mapCoordinates: {
    textAlign: 'center',
    marginBottom: 16,
    fontSize: 16,
    fontWeight: 'bold',
  },
  mapConfirmButton: {
    flex: 1,
  },
  mapButtonContainer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },
  currentLocationButton: {
    flex: 1,
  },
  mapCloseButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 24,
    zIndex: 1,
  },
  helpText: {
    fontSize: 13,
    color: '#666',
    fontStyle: 'italic',
    marginBottom: 12,
    lineHeight: 18,
  },
  mapHelpContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#f5f5f5',
    padding: 12,
    gap: 8,
  },
  mapHelpText: {
    flex: 1,
    fontSize: 13,
    color: '#666',
    lineHeight: 18,
  },
  reportCountContainer: {
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  reportCountText: {
    fontSize: 13,
    color: '#666',
    textAlign: 'center',
  },
  menuContainer: {
    marginLeft: 'auto',
  },
  mapHotspotsTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 8,
  },
});